import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { TsForm } from '@/components/form/Form';
import { SubmitCancelButtons } from '@/components/form/SubmitCancelButtons';
import { TextField } from '@/components/form/TextInput';
import { Modal, ModalProps } from '@/components/shared/modal/Modal';
import { handleMutationResult } from '@/helpers/toastHelper';
import { UpdateTemplate, useUpdateTemplateMutation } from '@/services/gql/graphql.generated';
import { strings } from '@/services/translation/strings';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';

interface RenameTemplateFormValues {
  name: string;
}

interface RenameTemplateModalProps extends ModalProps {
  currentName: string;
  buildUpdatePayload: (name: string) => UpdateTemplate;
  onRenamed: () => void;
}

export const RenameTemplateModal: FC<RenameTemplateModalProps> = ({
  open,
  onClose,
  currentName,
  buildUpdatePayload,
  onRenamed
}) => {
  const methods = useForm<RenameTemplateFormValues>({ defaultValues: { name: currentName } });
  const [{ fetching }, updateTemplate] = useUpdateTemplateMutation();

  useEffect(() => {
    if (open) {
      methods.reset({ name: currentName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSubmit = async (values: RenameTemplateFormValues) => {
    const result = await updateTemplate({ updateTemplate: buildUpdatePayload(values.name) });

    await handleMutationResult(result, {
      successMessage: strings.common.success,
      errorMessage: strings.daily.templates.renameModal.generalError,
      customSuccessCheck: data => !!data?.updateTemplate,
      onSuccess: () => {
        onClose();
        onRenamed();
      }
    });
  };

  return (
    <Modal open={open} onClose={onClose} header={strings.daily.templates.renameModal.title}>
      <SubmissionErrorBoundary>
        <TsForm methods={methods} onSubmit={onSubmit}>
          <div className="flex flex-col space-y-3 w-full">
            <TextField label={strings.daily.templates.renameModal.label} name="name" required />

            <div className="flex items-center justify-end gap-3">
              <SubmitCancelButtons
                disabled={!!methods.formState.errors.name?.message}
                onComplete={onClose}
                loading={fetching}
                submitText={strings.common.save}
              />
            </div>
          </div>
        </TsForm>
      </SubmissionErrorBoundary>
    </Modal>
  );
};
